import { ImagePreloader } from "@/Utils/ImagePreloader";
import { AudioManager } from "@/services/AudioManager";
import { logInfo, logError, logWarning } from "@/Utils/logger";
import { StartButtonMonitor } from "@/services/StartButtonMonitor";

export interface AssetLoadingState {
  imagesLoaded: boolean;
  audiosLoaded: boolean;
  imageRetryCount: number;
  audioRetryCount: number;
  isRetrying: boolean;
  failedImages: string[];
  failedAudios: string[];
}

export class AssetLoadingService {
  private static instance: AssetLoadingService;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 2000; // 2 seconds between retries

  /**
   * Watchdog for a `loadAudios()` call that never settles.
   *
   * A HANG IS NOT A FAILURE, and the retry/catch path below only handles
   * failures. Measured on a real iOS session (2026-08-22, dev v2.5.10): the
   * audio preload was kicked off 510ms BEFORE `AudioManager` finished
   * initializing HTML Audio, and that call then never resolved and never
   * rejected. `allAudiosLoaded` stayed false for 69 seconds with every other
   * Start-button gate green, and no retry ever ran because nothing threw. The
   * user saw a permanently disabled Start button with no message and no
   * control, and only escaped by navigating away and back — which happened to
   * re-run the preload after audio had initialized, completing 17/17 in 1.0s.
   *
   * 8s is ~8x the observed healthy load. It fires a RETRY rather than a
   * failure, so a merely-slow load is not turned into an error. This is the
   * same lesson the camera path learned in CHANGELOG [2.5.3]: guard a
   * potentially-unsettling promise with a timer, not a `Promise.race`.
   */
  private readonly AUDIO_LOAD_TIMEOUT_MS = 8000;
  /**
   * Generation counter for audio load attempts. Every attempt captures the
   * value at its start and checks it before acting on its own outcome; the
   * watchdog bumps it when it supersedes an attempt. Without this, every guard
   * in an attempt was a closure variable that nothing could invalidate, so a
   * hung `loadAudios()` that finally settled AFTER the watchdog had moved on
   * ran its post-await code as if it were current — flipping `audiosLoaded`
   * while the retry was mid-load, and (on rejection) starting a second retry
   * chain alongside the watchdog's.
   */
  private audioAttemptGeneration = 0;
  private readonly PAGE_RELOAD_DELAY = 3000; // 3 seconds before page reload

  private imagePreloader: ImagePreloader;
  private audioManager: AudioManager;
  private state: AssetLoadingState = {
    imagesLoaded: false,
    audiosLoaded: false,
    imageRetryCount: 0,
    audioRetryCount: 0,
    isRetrying: false,
    failedImages: [],
    failedAudios: [],
  };

  private onStateChangeCallback: ((state: AssetLoadingState) => void) | null =
    null;
  private onAllAssetsLoadedCallback: (() => void) | null = null;
  private isInitializing: boolean = false;

  private constructor() {
    this.imagePreloader = ImagePreloader.getInstance();
    this.audioManager = AudioManager.getInstance();
  }

  public static getInstance(): AssetLoadingService {
    if (!AssetLoadingService.instance) {
      AssetLoadingService.instance = new AssetLoadingService();
    }
    return AssetLoadingService.instance;
  }

  /**
   * Initialize asset loading with comprehensive retry mechanism
   */
  public async initializeAssets(force: boolean = false): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.isInitializing && !force) {
      logInfo(
        "Asset loading already in progress, skipping duplicate initialization"
      );
      return;
    }

    // If assets are already loaded, don't reinitialize unless forced
    if (this.areAllAssetsLoaded() && !force) {
      logInfo("Assets already loaded, skipping initialization");
      return;
    }

    if (force) {
      logInfo("Force initializing assets...");
      this.isInitializing = false; // Reset the flag
    }

    this.isInitializing = true;
    logInfo("Starting asset loading initialization...");

    this.updateState({
      imagesLoaded: false,
      audiosLoaded: false,
      imageRetryCount: 0,
      audioRetryCount: 0,
      isRetrying: false,
      failedImages: [],
      failedAudios: [],
    });

    // Start loading both assets concurrently
    const imageLoadingPromise = this.loadImagesWithRetry();
    const audioLoadingPromise = this.loadAudiosWithRetry();

    try {
      await Promise.all([imageLoadingPromise, audioLoadingPromise]);

      if (this.state.imagesLoaded && this.state.audiosLoaded) {
        logInfo("All assets loaded successfully!");
        this.onAllAssetsLoadedCallback?.();
      }
    } catch (error) {
      logError("Critical error during asset loading:", error as Error);
      await this.handleCriticalFailure();
    } finally {
      this.isInitializing = false;
    }
  }

  private async loadImagesWithRetry(): Promise<void> {
    return new Promise((resolve, reject) => {
      const attemptLoad = (retryCount: number) => {
        if (retryCount > 0) {
          this.updateState({
            isRetrying: true,
            imageRetryCount: retryCount,
          });
          logWarning(
            `Retrying image loading... Attempt ${retryCount}/${this.MAX_RETRIES}`
          );
          StartButtonMonitor.getInstance().onAssetLoadingRetry(
            "images",
            retryCount
          );
        }

        // Reset the image preloader for retry
        if (retryCount > 0) {
          this.imagePreloader.reset();
        }

        this.imagePreloader.onAllLoaded(() => {
          logInfo("All images loaded successfully");
          this.updateState({
            imagesLoaded: true,
            isRetrying: false,
          });
          StartButtonMonitor.getInstance().onAssetLoadingComplete("images");
          resolve();
        });

        // Set a timeout to detect if loading is stuck
        const timeout = setTimeout(() => {
          if (!this.state.imagesLoaded) {
            if (retryCount < this.MAX_RETRIES) {
              logWarning(
                `Image loading timeout. Retrying in ${this.RETRY_DELAY}ms...`
              );
              setTimeout(() => attemptLoad(retryCount + 1), this.RETRY_DELAY);
            } else {
              logError("Image loading failed after maximum retries");
              this.updateState({
                failedImages: ["timeout"],
                isRetrying: false,
              });
              StartButtonMonitor.getInstance().onAssetLoadingFailed(
                "images",
                new Error("Image loading timeout after retries")
              );
              reject(new Error("Image loading timeout after retries"));
            }
          }
        }, 15000); // 15 second timeout per attempt

        // Clear timeout if images load successfully
        const originalCallback = this.imagePreloader.onAllLoaded;
        this.imagePreloader.onAllLoaded = (callback) => {
          originalCallback.call(this.imagePreloader, () => {
            clearTimeout(timeout);
            callback();
          });
        };
      };

      attemptLoad(0);
    });
  }

  private async loadAudiosWithRetry(): Promise<void> {
    return new Promise((resolve, reject) => {
      const attemptLoad = async (retryCount: number) => {
        // Claimed FIRST, before the awaited cleanup below, so a superseded
        // attempt is already invalid while the new one is still resetting.
        const generation = ++this.audioAttemptGeneration;
        const superseded = () => generation !== this.audioAttemptGeneration;
        if (retryCount > 0) {
          this.updateState({
            isRetrying: true,
            audioRetryCount: retryCount,
          });
          logWarning(
            `Retrying audio loading... Attempt ${retryCount}/${this.MAX_RETRIES}`
          );
          StartButtonMonitor.getInstance().onAssetLoadingRetry(
            "audios",
            retryCount
          );

          // Clean up and reset audio manager for retry
          await this.audioManager.cleanupAndReset();
        }
        // On first attempt (retryCount === 0), DON'T cleanup
        // This allows audio loading started from user gesture to proceed
        // and preserves any callbacks that were set up

        // Set up callback BEFORE calling loadAudios to ensure it's not missed
        let callbackTriggered = false;
        // Armed BEFORE the await, because that is the whole point: if
        // `loadAudios()` never settles, nothing scheduled *after* it ever runs
        // — including the 100ms fallback below, which is why the stall was
        // silent. Disarmed by whichever completion path wins.
        let watchdog: ReturnType<typeof setTimeout> | null = setTimeout(() => {
          watchdog = null;
          if (callbackTriggered || superseded()) return;
          if (retryCount < this.MAX_RETRIES) {
            logWarning(
              "[AssetLoading] audio load did not settle — retrying",
              {
                timeoutMs: this.AUDIO_LOAD_TIMEOUT_MS,
                attempt: retryCount + 1,
                maxRetries: this.MAX_RETRIES,
                // Which step inside loadAudios() was in flight. The mechanism
                // behind BUG-0006 is not pinned — see the dossier — and this
                // is the field that pins it on the next occurrence.
                stage: this.audioManager.getLoadStage(),
              }
            );
            attemptLoad(retryCount + 1);
          } else {
            logError("[AssetLoading] audio load never settled after retries", {
              timeoutMs: this.AUDIO_LOAD_TIMEOUT_MS,
              maxRetries: this.MAX_RETRIES,
            });
            this.updateState({
              failedAudios: ["load_timeout"],
              isRetrying: false,
            });
            StartButtonMonitor.getInstance().onAssetLoadingFailed(
              "audios",
              new Error("audio load timeout")
            );
            reject(new Error("Audio loading timed out"));
          }
        }, this.AUDIO_LOAD_TIMEOUT_MS);

        const disarm = () => {
          if (watchdog !== null) {
            clearTimeout(watchdog);
            watchdog = null;
          }
        };

        this.audioManager.onAllAudiosLoaded(() => {
          if (!callbackTriggered && !superseded()) {
            callbackTriggered = true;
            disarm();
            logInfo("All audios loaded successfully");
            this.updateState({
              audiosLoaded: true,
              isRetrying: false,
            });
            StartButtonMonitor.getInstance().onAssetLoadingComplete("audios");
            resolve();
          }
        });

        try {
          await this.audioManager.loadAudios();

          // If loadAudios completed synchronously and callback wasn't triggered,
          // it means audios were already loaded - trigger callback manually
          setTimeout(() => {
            disarm();
            if (!callbackTriggered && !superseded()) {
              callbackTriggered = true;
              this.updateState({
                audiosLoaded: true,
                isRetrying: false,
              });
              StartButtonMonitor.getInstance().onAssetLoadingComplete("audios");
              resolve();
            }
          }, 100); // Small delay to allow async callback to fire first
        } catch (error) {
          disarm();
          // A superseded attempt's failure is not news: the watchdog already
          // moved on, and a retry from here would run a second chain alongside
          // the watchdog's, each cleanupAndReset()-ing the other's work.
          if (superseded()) return;
          if (retryCount < this.MAX_RETRIES) {
            logWarning(
              `Audio loading failed. Retrying in ${this.RETRY_DELAY}ms...`
            );
            setTimeout(() => attemptLoad(retryCount + 1), this.RETRY_DELAY);
          } else {
            logError("Audio loading failed after maximum retries");
            this.updateState({
              failedAudios: ["loading_error"],
              isRetrying: false,
            });
            StartButtonMonitor.getInstance().onAssetLoadingFailed(
              "audios",
              error
            );
            reject(new Error("Audio loading failed after retries"));
          }
        }
      };

      attemptLoad(0);
    });
  }

  private async handleCriticalFailure(): Promise<void> {
    const hasFailedImages = this.state.failedImages.length > 0;
    const hasFailedAudios = this.state.failedAudios.length > 0;

    if (hasFailedImages || hasFailedAudios) {
      await logError(
        "Critical asset loading failure detected. Preparing to reload page..."
      );

      // Show user-friendly message before reload
      const failureMessage = [];
      if (hasFailedImages) failureMessage.push("images");
      if (hasFailedAudios) failureMessage.push("audio files");

      const message = `Failed to load ${failureMessage.join(" and ")} after ${
        this.MAX_RETRIES
      } attempts. The page will reload automatically to try again.`;

      // You could show a toast/notification here if you have a notification system
      await logError(message);

      // Reload page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, this.PAGE_RELOAD_DELAY);
    }
  }

  /**
   * Manual retry for failed assets
   */
  public async retryFailedAssets(): Promise<void> {
    const hasFailedImages = this.state.failedImages.length > 0;
    const hasFailedAudios = this.state.failedAudios.length > 0;

    if (!hasFailedImages && !hasFailedAudios) {
      logInfo("No failed assets to retry");
      return;
    }

    logInfo("Manually retrying failed assets...");

    const promises: Promise<void>[] = [];

    if (hasFailedImages) {
      promises.push(this.loadImagesWithRetry());
    }

    if (hasFailedAudios) {
      promises.push(this.loadAudiosWithRetry());
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      logError("Manual retry failed:", error as Error);
      await this.handleCriticalFailure();
    }
  }

  /**
   * Check if all assets are loaded
   */
  public areAllAssetsLoaded(): boolean {
    return this.state.imagesLoaded && this.state.audiosLoaded;
  }

  /**
   * Wait for all assets to be loaded (no timeout, continuous retry)
   */
  public async waitForAssetsToLoad(): Promise<boolean> {
    if (this.areAllAssetsLoaded()) {
      return true;
    }

    return new Promise((resolve) => {
      const checkAssets = () => {
        if (this.areAllAssetsLoaded()) {
          resolve(true);
        } else {
          // Check again in 100ms
          setTimeout(checkAssets, 100);
        }
      };

      checkAssets();
    });
  }

  /**
   * Get current loading state
   */
  public getState(): AssetLoadingState {
    return { ...this.state };
  }

  /**
   * Register callback for state changes
   */
  public onStateChange(callback: (state: AssetLoadingState) => void): void {
    this.onStateChangeCallback = callback;
  }

  /**
   * Register callback for when all assets are loaded
   */
  public onAllAssetsLoaded(callback: () => void): void {
    this.onAllAssetsLoadedCallback = callback;
  }

  private updateState(updates: Partial<AssetLoadingState>): void {
    this.state = { ...this.state, ...updates };
    this.onStateChangeCallback?.(this.state);
  }

  /**
   * Reset the service state
   */
  public reset(): void {
    this.isInitializing = false;
    this.state = {
      imagesLoaded: false,
      audiosLoaded: false,
      imageRetryCount: 0,
      audioRetryCount: 0,
      isRetrying: false,
      failedImages: [],
      failedAudios: [],
    };
    this.onStateChangeCallback?.(this.state);
  }
}
